<template>
  <v-card
    :data-pw="dataPw"
    title="Create Institution"
  >
    <v-card-text v-if="allowed">
      <v-form @submit.prevent @submit="handleSubmit">
        <tenant-search-autocomplete
          v-model="selectedTenant"
          entity-type="tenant"
          use-store
        />
        <v-text-field
          :label="$t('common.title')"
          v-model="title"
          variant="outlined"
          :rules="titleRules"
          data-pw="titleField"
        />
      </v-form>
    </v-card-text>

    <v-card-text v-else>
      <v-alert type="error">
        {{ $t("common.no_permission") }}
      </v-alert>
    </v-card-text>

    <v-card-actions class="justify-end">
      <v-btn-secondary @click="$emit('close')">
        {{ $t('buttons.cancel') }}
      </v-btn-secondary>
      <v-btn-primary :disabled="!canCreate" @click="handleSubmit">
        {{ $t("buttons.create") }}
      </v-btn-primary>
    </v-card-actions>
  </v-card>
</template>

<script setup lang="ts">
import {computed, ref} from "vue";
import {useRouter} from "vue-router";
import {useI18n} from "vue-i18n";
import yapi from "@/lib/yapi";
import {isYapiError, YapiError} from "@YenzaCT/sdk";
import {useGlobalStore} from "@/store";
import TenantSearchAutocomplete from "@/components/FormFields/TenantSearchAutocomplete.vue";

interface Props {
  allowed?: boolean;
  dataPw?: string;
}

withDefaults(defineProps<Props>(), {
  allowed: true,
  dataPw: "createInstitutionCard"
});

const emit = defineEmits<{
  close: []
}>();

const { t } = useI18n();
const router = useRouter();
const store = useGlobalStore();
const selectedTenant = ref<string>();

const title = ref("");

const titleRules = [(v: string) => !!v || t("common.title_required")];

const canCreate = computed(() => !!title.value && !!selectedTenant.value);

async function handleSubmit() {
  if (!selectedTenant.value) return;

  try {
    store.networkBusy = true;
    const institution = await yapi.admin.institution.create(
      title.value,
      selectedTenant.value
    );

    store.showSnackbar(t("components.CreateCards.CreateTenantInstitutionCohort.created_message"));
    await router.push({
      name: "institutionDetail",
      params: { institutionId: institution.data._id }
    });

    emit("close");
  } catch (e) {
    if (isYapiError(e)) {
      const error = e as YapiError;
      store.showSnackbar(error.message);
    } else {
      store.showSnackbar(t("components.CreateCards.CreateTenantInstitutionCohort.error_message"));
    }
  } finally {
    store.networkBusy = false;
  }
}
</script>
